import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Clock, Globe, Loader2, ShieldAlert, UserPlus, XCircle } from 'lucide-react';
import { API } from '../utils/api';
import { PAYMIG_LOGO_B64 } from '../utils/paymigLogo';
import { readSwishReturn } from '../utils/swishReturn';
import { useSwishReturn } from '../hooks/useSwishReturn';

const GUEST_ENDPOINTS = {
  callback: (id) => `${API}/guest/payment/${id}/callback`,
  status: (id, nonce) => `${API}/guest/payment/${id}/status/${nonce}`,
};

const STATUS = {
  loading: { icon: <Loader2 className="h-14 w-14 text-teal-600 animate-spin" />, title: 'guestPayment.processingPayment', desc: 'guestPayment.pleaseWait' },
  success: { icon: <CheckCircle className="h-14 w-14 text-emerald-500" />, title: 'guestPayment.paymentSuccessful', desc: 'guestPayment.paymentReceived' },
  failed: { icon: <XCircle className="h-14 w-14 text-red-500" />, title: 'guestPayment.paymentCancelled', desc: 'guestPayment.cancelledDesc' },
  expired: { icon: <XCircle className="h-14 w-14 text-red-500" />, title: 'swishReturn.expiredTitle', desc: 'swishReturn.expiredDesc' },
  pending: { icon: <Clock className="h-14 w-14 text-amber-500 animate-pulse" />, title: 'swishReturn.pendingTitle', desc: 'swishReturn.pendingDesc' },
  suspicious: { icon: <ShieldAlert className="h-14 w-14 text-red-600" />, title: 'swishReturn.suspiciousTitle', desc: 'swishReturn.suspiciousHint' },
  notFound: { icon: <XCircle className="h-14 w-14 text-red-500" />, title: 'swishReturn.notFoundTitle', desc: 'swishReturn.notFoundDesc' },
  unknown: { icon: <XCircle className="h-14 w-14 text-red-500" />, title: 'guestPayment.somethingWentWrong', desc: 'guestPayment.errorDesc' },
};
const SUSPICIOUS_COPY = { payee: 'swishReturn.suspiciousPayee', amount: 'swishReturn.suspiciousAmount', other: 'swishReturn.suspiciousOther' };

function InfoRow({ label, value, testId }) {
  return (
    <div className="flex justify-between" data-testid={testId}>
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 font-medium">{value}</span>
    </div>
  );
}

export default function GuestPaymentReturn() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const params = useParams();
  const location = useLocation();

  const info = useMemo(() => {
    const parsed = readSwishReturn(params, location.search);
    return {
      ...parsed,
      paymentId: parsed.paymentId || sessionStorage.getItem('guest_payment_id'),
      nonce: parsed.nonce || sessionStorage.getItem('guest_payment_nonce'),
    };
  }, [params, location.search]);
  const stored = useMemo(() => ({
    amount: sessionStorage.getItem('guest_payment_amount'),
    recipient: sessionStorage.getItem('guest_payment_recipient'),
  }), []);
  useEffect(() => {
    ['guest_payment_id', 'guest_payment_nonce', 'guest_payment_amount', 'guest_payment_recipient'].forEach((k) => sessionStorage.removeItem(k));
  }, []);

  const { state, details, reason, ticketReported } = useSwishReturn(info, GUEST_ENDPOINTS);
  const view = info.paymentId && info.nonce ? state : 'unknown';
  const cfg = STATUS[view] || STATUS.unknown;
  const amount = details?.amount ?? stored.amount;
  const recipient = details?.recipient_name || details?.sub_account_name || stored.recipient;
  const subAccount = details?.sub_account_name;
  // Display only: verification always uses the server-stored intent.
  const payee = details?.recipient_alias || info.payee;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-testid="guest-payment-return-page">
      <div className="flex items-center gap-3 mb-8">
        <img src={PAYMIG_LOGO_B64} alt="PayMig" className="w-10 h-10 rounded-lg" />
        <span className="text-xl font-bold text-gray-900 tracking-tight">PayMig</span>
        <button
          onClick={() => i18n.changeLanguage(i18n.language === 'sv' ? 'en' : 'sv')}
          className="ml-3 flex items-center gap-1 px-2 py-1 rounded-md bg-gray-200 hover:bg-gray-300 text-xs text-gray-700 transition-colors"
          data-testid="lang-toggle"
        >
          <Globe className="h-3 w-3" />
          {i18n.language === 'sv' ? 'EN' : 'SV'}
        </button>
      </div>

      <Card className="w-full max-w-md border-gray-200 shadow-lg" data-testid="guest-return-card" data-state={view}>
        <CardContent className="pt-8 pb-6 text-center space-y-5">
          <div className="flex justify-center">{cfg.icon}</div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900" data-testid="return-status-title">{t(cfg.title)}</h2>
            {view === 'suspicious' && (
              <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-left text-sm text-red-800" data-testid="verification-failed-banner">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t(SUSPICIOUS_COPY[reason] || SUSPICIOUS_COPY.other)}</span>
              </div>
            )}
            <p className="text-sm text-gray-500" data-testid="return-status-desc">{t(cfg.desc)}</p>
          </div>

          {(amount || recipient || payee) && (
            <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 border border-gray-200" data-testid="return-payment-info">
              {amount && <InfoRow label={t('guestPayment.amount')} value={`${amount} SEK`} testId="return-amount" />}
              {recipient && <InfoRow label={t('guestPayment.to')} value={recipient} testId="return-recipient" />}
              {subAccount && subAccount !== recipient && <InfoRow label={t('guestPayment.for')} value={subAccount} testId="return-sub-account" />}
              {payee && <InfoRow label={t('swishReturn.swishNumber')} value={payee} testId="return-payee" />}
            </div>
          )}

          {ticketReported && <p className="text-xs text-amber-800" data-testid="support-notified-note">{t('swishReturn.supportNotified')}</p>}

          {view === 'success' && (
            <div className="pt-2 space-y-3" data-testid="signup-cta">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                <p className="text-sm text-teal-700 font-medium mb-1">{t('guestPayment.trackPayments')}</p>
                <p className="text-xs text-gray-500">{t('guestPayment.createAccountDesc')}</p>
              </div>
              <Button onClick={() => nav('/')} className="w-full h-11 bg-teal-600 hover:bg-teal-500 text-white font-medium" data-testid="signup-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                {t('guestPayment.createAccount')}
              </Button>
            </div>
          )}

          {view !== 'loading' && (
            <button onClick={() => window.close()} className="text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="close-btn">
              {t('guestPayment.closePage')}
            </button>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 text-center space-y-1">
        <p className="text-xs text-gray-400">{t('guestPayment.poweredBy')}</p>
        <p className="text-xs text-gray-500">support@paymig.app</p>
      </div>
    </div>
  );
}
